import { getConfig } from '@config';
import type { ExperimentClient, Exposure, ExposureTrackingProvider } from '@amplitude/experiment-js-client';
import type { EventGroupContext } from '@shared/core/analytics/segment/eventEnrichment';
import { getPlatformProperties, getSharedContextProperties, getViewportSize } from '@shared/core/analytics/segment/eventEnrichment';
import { getAmplitudeSessionId } from '@shared/core/analytics/segment/getTrackerIds';
import { addAction, addError } from '@shared/utils/logger';
import { isPageUnloading } from '@shared/utils/pageUnloading';
import { withWindow } from '@shared/utils/withWindow';
import { segment } from '@static/js/segment';
import { getAnalyticsId } from '@shared/core/analytics/segment/getAnalyticsId';

import { LEGACY_EVENT_GROUP_TYPE, POSTGRES_EVENT_GROUP_TYPE, readAnonymousIdFromCookie } from './amplitude.utils';

const JOY_EVENT_GROUP_KEY = LEGACY_EVENT_GROUP_TYPE;

export const AMPLITUDE_EXPOSURE_FLUSH_INTERVAL_MS = 1000;

// Max timer-flush deferrals while waiting for fetchUser() to assign the SDK user; then send best-effort (see flush()).
const MAX_EXPOSURE_FLUSH_DEFERRALS = 3;

export const AMPLITUDE_EXPOSURE_RETRY_DELAY_MS = 2000;

const MAX_REPORTED_TEXT_LENGTH = 200;

// Amplitude rejects an insert_id of 256 characters or more.
const MAX_INSERT_ID_LENGTH = 255;

type SendResult = Readonly<{ ok: true }> | Readonly<{ ok: false; httpStatus: number; responseBody: string }> | Readonly<{ ok: false; error: unknown }>;

type SendFailure = Exclude<SendResult, { ok: true }>;

type BatchDetails = Readonly<{ attempts: number; eventCount: number; bodyBytes: number; urlHost: string }>;

// A rejected payload (400, 413, ...) fails the same way on retry; 408 and 429 are transient and can clear.
const isPermanentFailure = (failure: SendFailure): boolean =>
  'httpStatus' in failure && failure.httpStatus >= 400 && failure.httpStatus < 500 && failure.httpStatus !== 408 && failure.httpStatus !== 429;

const isOnline = (): boolean => (typeof navigator === 'undefined' ? true : navigator.onLine);

const isHidden = (): boolean => typeof document !== 'undefined' && document.visibilityState === 'hidden';

// Offline, hidden or unloading, a retry is unlikely to land; keepalive already carries the first request past unload.
const canRetryNow = (): boolean => isOnline() && !isHidden() && !isPageUnloading();

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const truncate = (text: string): string => text.slice(0, MAX_REPORTED_TEXT_LENGTH);

const safeString = (value: unknown): string => {
  try {
    return String(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
};

// Rejections can be anything, including null-prototype objects whose String() throws.
const describeError = (error: unknown): Readonly<{ errorName: string; errorMessage: string }> => {
  try {
    if (error !== null && typeof error === 'object' && 'message' in error) {
      const { name, message } = error as { name?: unknown; message?: unknown };
      return { errorName: name === undefined ? 'object' : safeString(name), errorMessage: safeString(message) };
    }
    return { errorName: typeof error, errorMessage: safeString(error) };
  } catch {
    return { errorName: 'unknown', errorMessage: '' };
  }
};

const toError = (error: unknown): Error => (error instanceof Error ? error : new Error(describeError(error).errorMessage));

const getUrlHost = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
};

const readResponseSnippet = async (response: Response): Promise<string> => {
  try {
    return truncate(await response.text());
  } catch {
    return '';
  }
};

type ExposurePayload = Readonly<{
  flag_key: string;
  variant?: string;
  experiment_key?: string;
}>;

export type QueuedExposure = Readonly<{
  exposure: ExposurePayload;
  time: number;
}>;

type AmplitudeEvent = Readonly<{
  event_type: string;
  user_id?: string;
  device_id?: string;
  time: number;
  session_id?: number;
  insert_id?: string;
  event_properties: Record<string, unknown>;
  groups?: Readonly<Record<string, ReadonlyArray<string>>>;
}>;

type SharedContext = Readonly<Record<string, unknown>>;

// cyrb53: synchronous 53-bit string hash, enough to keep distinct long tuples from colliding.
const hashString = (input: string): string => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i++) {
    const code = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

// Over the limit, a plain slice would drop the tail (variant, experiment key) and merge distinct exposures into one insert_id.
const buildInsertId = (parts: ReadonlyArray<string | number>): string => {
  const joined = parts.join('|');
  if (joined.length <= MAX_INSERT_ID_LENGTH) return joined;
  const hash = hashString(joined);
  return `${joined.slice(0, MAX_INSERT_ID_LENGTH - hash.length - 1)}|${hash}`;
};

function buildContextProperties(sharedContext: SharedContext, groupContext: EventGroupContext): SharedContext {
  const contextProperties: Record<string, unknown> = {};

  Object.entries(sharedContext).forEach(([key, value]) => {
    contextProperties[`context.${key}`] = value;
  });

  Object.entries(groupContext).forEach(([key, value]) => {
    contextProperties[`context.${key}`] = value;
  });

  return contextProperties;
}

export type EnrichmentContext = Readonly<{
  analyticsId: string;
  platformProperties: Record<string, unknown>;
  viewportSize: 'mobile' | 'desktop';
  sessionId?: number;
  sharedContext: SharedContext;
  userId?: string;
  deviceId?: string;
  firebaseEventId?: string;
  /** The event UUID from the SDK user's `Event` group. */
  postgresEventId?: string;
}>;

function firstGroupValue(groups: Record<string, unknown>, groupType: string): string | undefined {
  const group = groups[groupType];
  return Array.isArray(group) && group.length > 0 && typeof group[0] === 'string' ? group[0] : undefined;
}

export function buildAmplitudeEvent(item: QueuedExposure, enrichment: EnrichmentContext): AmplitudeEvent {
  const groupContext: EventGroupContext = {
    firebaseEventId: enrichment.firebaseEventId,
    // Same property name as Track and Page, so analysts query one name for the event UUID.
    postgresEventId: enrichment.postgresEventId,
  };

  const contextProperties = buildContextProperties(enrichment.sharedContext, groupContext);

  const eventProperties = {
    analyticsId: enrichment.analyticsId,
    category: '$exposure',
    ...enrichment.platformProperties,
    viewportSize: enrichment.viewportSize,
  };

  // Amplitude's group-bucketed experiment analysis reads group identity from the top-level `groups` on the exposure event, not from buried event properties. Attaching it keeps anonymous guests (the majority of accommodations traffic) in JoyEvent group analysis. Omit entirely when unknown — never send empty arrays.
  // `Event` is added beside `JoyEvent` with the same rule, keyed by the event UUID.
  const groups = {
    ...(enrichment.firebaseEventId ? { [JOY_EVENT_GROUP_KEY]: [enrichment.firebaseEventId] } : {}),
    ...(enrichment.postgresEventId ? { [POSTGRES_EVENT_GROUP_TYPE]: [enrichment.postgresEventId] } : {}),
  };

  return {
    event_type: '$exposure',
    user_id: enrichment.userId,
    device_id: enrichment.deviceId,
    time: item.time,
    session_id: enrichment.sessionId,
    // Stable per exposure so Amplitude dedupes a resend whose first response was lost.
    insert_id: buildInsertId([enrichment.deviceId ?? enrichment.userId ?? '', item.time, item.exposure.flag_key, item.exposure.variant ?? '', item.exposure.experiment_key ?? '']),
    ...(Object.keys(groups).length > 0 ? { groups } : {}),
    event_properties: {
      ...eventProperties,
      ...contextProperties,
      // Place at the end so these fields always take precedence if keys collide.
      // [Experiment] Experiment Key disambiguates re-runs of the same flag (exp-2, exp-3, ...) —
      // Amplitude's experiment-analysis filter requires it whenever a flag has been run more than once.
      '[Experiment] Flag Key': item.exposure.flag_key,
      '[Experiment] Variant': item.exposure.variant,
      '[Experiment] Experiment Key': item.exposure.experiment_key,
    },
  };
}

export class ExposureQueue {
  private queue: QueuedExposure[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private flushDeferrals = 0;
  private readonly flushInterval = AMPLITUDE_EXPOSURE_FLUSH_INTERVAL_MS;

  constructor(private readonly client: ExperimentClient) {
    // Force-flush on unload: the page is leaving, so send even if the SDK user/group isn't ready yet.
    withWindow(globalWindow => {
      globalWindow.addEventListener('beforeunload', () => {
        this.flush(true, true);
      });
    });

    // Flush when page becomes hidden — force it for the same reason as beforeunload.
    withWindow(globalWindow => {
      globalWindow.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush(true);
        }
      });
    });
  }

  enqueue(item: QueuedExposure): void {
    this.queue.push(item);
    this.startTimer();
  }

  private startTimer(): void {
    if (this.flushTimer !== null) return;

    this.flushTimer = setTimeout(() => {
      this.flush();
    }, this.flushInterval);
  }

  private clearTimer(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * @param force - send even if the SDK user is not assigned yet
   * @param pageLeaving - the page is unloading, so this is the last chance to send
   */
  private flush(force = false, pageLeaving = false): void {
    this.clearTimer();

    if (this.queue.length === 0) return;

    // Exposures go straight to Amplitude, so they need the statistics consent Segment's Amplitude
    // destination needs. They wait for the page to decide it, even while the tab is hidden; only a
    // page that is leaving decides from the cookie instead.
    const consent = segment.getActiveConsent() ?? (pageLeaving ? segment.readCookieConsent() : null);
    if (!consent) {
      this.startTimer();
      return;
    }
    if (!consent.statistics) {
      this.queue = [];
      return;
    }

    // Defer until fetchUser() assigns the SDK user so SSR-hydration exposures keep their JoyEvent group; getUser() is falsy (typed non-null but null pre-setUser) until then.
    if (!force && !this.client.getUser() && this.flushDeferrals < MAX_EXPOSURE_FLUSH_DEFERRALS) {
      this.flushDeferrals += 1;
      this.startTimer();
      return;
    }

    this.flushDeferrals = 0;
    const itemsToSend = this.queue;
    this.queue = [];

    // Fire-and-forget: sendBatch reports its own failures; this catch is a last resort.
    this.sendBatch(itemsToSend).catch(error => {
      addError(toError(error), { context: 'AmplitudeExposureBatchSend', eventCount: itemsToSend.length });
    });
  }

  private async sendBatch(items: QueuedExposure[]): Promise<void> {
    // Only send from browser; skip on SSR
    const shouldSend = withWindow(() => true, false);
    if (!shouldSend) {
      return;
    }

    let url: string;
    let body: string;
    try {
      const config = getConfig();
      url = config.amplitudeAnalyticsApiUrl;
      body = JSON.stringify({
        api_key: config.amplitudeAnalyticsApiKey,
        events: this.buildEvents(items),
      });
    } catch (error) {
      addError(toError(error), { context: 'AmplitudeExposureBatchBuild', eventCount: items.length });
      return;
    }

    let attempts = 1;
    let result = await this.post(url, body);

    // One delayed retry is enough for exposure telemetry. Conditions are re-checked after the wait.
    if (!result.ok && !isPermanentFailure(result) && canRetryNow()) {
      await wait(AMPLITUDE_EXPOSURE_RETRY_DELAY_MS);
      if (canRetryNow()) {
        attempts += 1;
        result = await this.post(url, body);
      }
    }

    if (!result.ok) {
      this.reportFailure(result, { attempts, eventCount: items.length, bodyBytes: new Blob([body]).size, urlHost: getUrlHost(url) });
    }
  }

  private buildEvents(items: QueuedExposure[]): AmplitudeEvent[] {
    const user = this.client.getUser();

    // Extract firebaseEventId from experiment client groups
    const groups = user?.groups || {};
    const firebaseEventId = firstGroupValue(groups, JOY_EVENT_GROUP_KEY);
    const postgresEventId = firstGroupValue(groups, POSTGRES_EVENT_GROUP_TYPE);

    // Fetch all enrichment context once for the entire batch
    const enrichment: EnrichmentContext = {
      analyticsId: getAnalyticsId(),
      platformProperties: getPlatformProperties(),
      viewportSize: getViewportSize(),
      sessionId: getAmplitudeSessionId(),
      sharedContext: getSharedContextProperties(),
      userId: user?.user_id,
      // Prefer the SDK-set device_id; before fetchUser() resolves, fall back to the same cookie
      // SSR used (joy_anonymous_id) so bucketing-identity and exposure-identity agree.
      // segment.getAnonymousId() remains the final safety net.
      deviceId: user?.device_id ?? readAnonymousIdFromCookie() ?? segment.getAnonymousId(),
      firebaseEventId,
      postgresEventId,
    };

    return items.map(item => buildAmplitudeEvent(item, enrichment));
  }

  private async post(url: string, body: string): Promise<SendResult> {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
        // keepalive ensures the request completes even if the page unloads (e.g., tab close, navigation)
        // so we don't drop telemetry
        // see https://fetch.spec.whatwg.org/#http-network-or-cache-fetch
        keepalive: true,
      });

      if (response.ok) {
        return { ok: true };
      }
      return { ok: false, httpStatus: response.status, responseBody: await readResponseSnippet(response) };
    } catch (error) {
      return { ok: false, error };
    }
  }

  private reportFailure(failure: SendFailure, { attempts, eventCount, bodyBytes, urlHost }: BatchDetails): void {
    if ('httpStatus' in failure) {
      const { httpStatus, responseBody } = failure;
      if (isPermanentFailure(failure)) {
        // Status only in the message so New Relic groups these together.
        addError(`Amplitude exposure batch rejected: HTTP ${httpStatus}`, { httpStatus, responseBody, bodyBytes, urlHost, attempts, eventCount });
        return;
      }
      this.reportLostBatch({ errorName: 'HttpError', errorMessage: `HTTP ${httpStatus}`, httpStatus, responseBody, attempts, eventCount });
      return;
    }

    this.reportLostBatch({ ...describeError(failure.error), attempts, eventCount });
  }

  private reportLostBatch(details: Readonly<{ errorName: string; errorMessage: string; httpStatus?: number; responseBody?: string; attempts: number; eventCount: number }>): void {
    addAction('AmplitudeExposureBatchFailed', {
      ...details,
      errorMessage: truncate(details.errorMessage),
      online: isOnline(),
      pageUnloading: isPageUnloading(),
      hidden: isHidden(),
    });
  }
}

export function withAmplitudeExposureTracking(createClient: (exposureTrackingProvider: ExposureTrackingProvider) => ExperimentClient): ExperimentClient {
  // Defer binding until after client/queue are created to avoid circular init concerns
  let enqueueExposure: (exposure: Exposure) => void = () => {};

  const trackExposure = (exposure: Exposure) => enqueueExposure(exposure);

  const exposureTrackingProvider: ExposureTrackingProvider = { track: trackExposure };
  const client = createClient(exposureTrackingProvider);
  const queue = new ExposureQueue(client);

  enqueueExposure = (exposure: Exposure) => {
    queue.enqueue({
      exposure: { flag_key: exposure.flag_key, variant: exposure.variant, experiment_key: exposure.experiment_key },
      time: Date.now(),
    });
  };

  return client;
}
